// SEO landing page definitions. Each drives <Landing /> via /:slug route.
// Keep copy natural and genuinely useful — no keyword stuffing.

const STORE_LINE = "Visit our boutique at 183 West Golf Rd, Schaumburg, IL — serving Chicago and the surrounding suburbs — or shop online with shipping across the USA.";

// City pages target "indian clothing store near <city>" searches. Each gets
// unique intro copy with real drive context — never thin doorway pages.
// shipOnly: for out-of-state areas (no DoorDash same-day; lead with shipping).
const cityPage = ({ city, drive, blurb, shipOnly = false }) => ({
  seoTitle: shipOnly
    ? `Indian Clothing in ${city} — Kurtis, Sarees & Lehengas Shipped Fast`
    : `Indian Clothing Store near ${city}, IL — Kurtis, Sarees & Lehengas`,
  metaDescription: shipOnly
    ? `Shop Indian ethnic wear in ${city}: kurtis, sarees, lehengas & party wear shipped in 2–5 days from our Chicago-area boutique. Free US shipping over $75.`
    : `Looking for Indian ethnic wear near ${city}, IL? Kurti Connection in Schaumburg is about ${drive} away — kurtis, sarees, lehengas & party wear. Same-day DoorDash delivery available.`,
  kicker: shipOnly ? `Shipping Across ${city}` : `Serving ${city} & the Northwest Suburbs`,
  h1: shipOnly ? `Indian Ethnic Wear, Shipped Across ${city}` : `Indian Ethnic Wear near ${city}, Illinois`,
  hero: "https://images.unsplash.com/photo-1769063382706-8156b3b33eac?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  intro: [
    blurb,
    shipOnly
      ? "Every order ships from our Schaumburg, IL boutique in 2–5 days — free US shipping over $75, easy 14-day returns, and WhatsApp help with sizing before you buy."
      : `${STORE_LINE} Same-day delivery via DoorDash covers ${city} and anywhere within 25 miles of the boutique.`,
  ],
  collection: "women",
  quickLinks: [
    { label: "Kurtis", to: "/shop?collection=kurtis" },
    { label: "Sarees", to: "/shop?collection=sarees" },
    { label: "Lehengas", to: "/shop?collection=lehenga-choli" },
    { label: "Party Wear", to: "/shop?collection=reception-cocktail-party" },
    { label: "Under $99", to: "/shop?collection=under-99" },
  ],
  faqs: shipOnly ? [
    { q: `How fast is shipping to ${city}?`, a: `Orders ship from our Schaumburg, IL boutique and typically arrive in ${city} within 2–5 days. Free US shipping on orders over $75.` },
    { q: "What if the size doesn't fit?", a: "Easy 14-day returns on unused, unaltered items. Message us on WhatsApp before ordering and we'll help you pick the right size the first time." },
    { q: "Can I visit the store in person?", a: "Yes — 183 West Golf Rd, Schaumburg, IL. Many customers make the drive for weddings and festivals; the full collection is out to try on." },
  ] : [
    { q: `Is there an Indian clothing store near ${city}?`, a: `Yes — Kurti Connection at 183 West Golf Rd, Schaumburg is about ${drive} from ${city}. We're the closest full Indian boutique for most Northwest suburbs, with kurtis, sarees, lehengas, menswear and kids' wear.` },
    { q: `Do you deliver to ${city}?`, a: `Yes. Same-day delivery via DoorDash covers ${city} (anywhere within 25 miles of our Schaumburg boutique) — order through our DoorDash store. We also ship across the USA in 2–5 days, free over $75.` },
    { q: "What are your store hours?", a: "Monday 2:00–7:00 PM, Tuesday–Friday 11:30 AM–7:00 PM, and Saturday & Sunday 11:30 AM–6:00 PM." },
  ],
  local: true,
});

const CITY_PAGES = {
  "indian-clothing-lake-zurich-il": cityPage({
    city: "Lake Zurich",
    drive: "a 15-minute drive",
    blurb: "Lake Zurich shoppers don't need to drive to Devon Avenue for authentic Indian wear. Our Schaumburg boutique is a straight shot down Route 12 — about 15 minutes — with hundreds of kurtis, sarees, lehengas and wedding outfits in stock to try on in person.",
  }),
  "indian-clothing-mundelein-il": cityPage({
    city: "Mundelein",
    drive: "a 20-minute drive",
    blurb: "From Mundelein, you're about 20 minutes from the Northwest suburbs' most-loved Indian boutique. Whether it's a last-minute wedding-guest outfit or festive wear for Diwali, try everything on in person — no waiting for shipping, no size-chart guesswork.",
  }),
  "indian-clothing-vernon-hills-il": cityPage({
    city: "Vernon Hills",
    drive: "a 15-minute drive",
    blurb: "Vernon Hills is practically next door — hop on Milwaukee Avenue and you're at our Schaumburg boutique in about 15 minutes. Browse kurtis, sarees, lehengas and party wear in person, or get same-day delivery right to your door via DoorDash.",
  }),
  "indian-clothing-crystal-lake-il": cityPage({
    city: "Crystal Lake",
    drive: "a 30-minute drive",
    blurb: "Crystal Lake shoppers: skip the trip into Chicago. Our Schaumburg boutique is about 30 minutes down Route 14 — the closest place to browse a full range of Indian ethnic wear in person, from everyday kurtis to bridal lehengas.",
  }),
  "indian-clothing-palatine-il": cityPage({
    city: "Palatine",
    drive: "a 10-minute drive",
    blurb: "From Palatine you're barely 10 minutes from our Schaumburg boutique — closer than your grocery run. Drop in to try kurtis, sarees and lehengas in person, or order on DoorDash and have your outfit the same day.",
  }),
  "indian-clothing-hoffman-estates-il": cityPage({
    city: "Hoffman Estates",
    drive: "a 10-minute drive",
    blurb: "Hoffman Estates neighbors — our boutique at 183 West Golf Rd is minutes away. Indian ethnic wear for the whole family: women's kurtis and lehengas, men's kurtas, kids' outfits, plus same-day DoorDash delivery when you're in a rush.",
  }),
  "indian-clothing-arlington-heights-il": cityPage({
    city: "Arlington Heights",
    drive: "a 15-minute drive",
    blurb: "Arlington Heights shoppers are about 15 minutes from the area's favorite Indian boutique. Try everything on in person — kurtis, sarees, Anarkalis, lehengas — with help from staff who know the pieces inside out.",
  }),
  "indian-clothing-buffalo-grove-il": cityPage({
    city: "Buffalo Grove",
    drive: "a 15-minute drive",
    blurb: "From Buffalo Grove, authentic Indian ethnic wear is 15 minutes away in Schaumburg. Wedding-guest outfits, festive sarees, everyday kurtis — all in stock to try today, with same-day DoorDash delivery if you'd rather stay home.",
  }),
  "indian-clothing-schaumburg-il": cityPage({
    city: "Schaumburg",
    drive: "just minutes",
    blurb: "You're already here — our boutique at 183 West Golf Rd is Schaumburg's home for Indian ethnic wear. Walk in and browse the full collection in person, or order on DoorDash and have it the same day.",
  }),
  "indian-clothing-south-barrington-il": cityPage({
    city: "South Barrington",
    drive: "a 10-minute drive",
    blurb: "South Barrington is one of our closest neighbors — about 10 minutes to the boutique. Skip the Devon Avenue trip: kurtis, sarees, lehengas and menswear, all in stock to try on today.",
  }),
  "indian-clothing-barrington-il": cityPage({
    city: "Barrington",
    drive: "a 15-minute drive",
    blurb: "From Barrington, our Schaumburg boutique is an easy 15-minute drive down Route 59 or Northwest Highway. Full Indian ethnic wear for the family — plus same-day DoorDash delivery straight to Barrington.",
  }),
  "indian-clothing-elgin-il": cityPage({
    city: "Elgin",
    drive: "a 20-minute drive",
    blurb: "Elgin shoppers: hop on Route 20 and you're at our Schaumburg boutique in about 20 minutes. One of the largest Indian ethnic wear selections in the suburbs — kurtis to bridal lehengas — with same-day DoorDash delivery back to Elgin.",
  }),
  "indian-clothing-elk-grove-village-il": cityPage({
    city: "Elk Grove Village",
    drive: "a 10-minute drive",
    blurb: "Elk Grove Village is practically around the corner — 10 minutes to racks of kurtis, sarees, Anarkalis and lehengas you can actually try on. In a hurry? DoorDash brings it to your door the same day.",
  }),
  "indian-clothing-cary-il": cityPage({
    city: "Cary",
    drive: "a 30-minute drive",
    blurb: "From Cary, authentic Indian ethnic wear is a 30-minute drive — no need to head into the city. Browse the full collection in Schaumburg, or order online with fast 2–5 day shipping and easy returns.",
  }),
  "indian-clothing-naperville-il": cityPage({
    city: "Naperville",
    drive: "a 30-minute drive",
    blurb: "Naperville shoppers make the 30-minute trip to our Schaumburg boutique for the selection — hundreds of kurtis, sarees and lehengas in every size, plus menswear and kids. Prefer to stay home? We ship to Naperville in 2–5 days.",
  }),
  "indian-clothing-roselle-il": cityPage({
    city: "Roselle",
    drive: "a 10-minute drive",
    blurb: "Roselle neighbors — Indian ethnic wear is 10 minutes away at our Schaumburg boutique. Everyday kurtis to wedding lehengas, tried on in person, with same-day DoorDash delivery when the event crept up on you.",
  }),
  "indian-clothing-carpentersville-il": cityPage({
    city: "Carpentersville",
    drive: "a 20-minute drive",
    blurb: "From Carpentersville, take Route 25 or 31 down to Schaumburg — about 20 minutes to the Northwest suburbs' full Indian boutique. Kurtis, sarees, lehengas, menswear and kids' wear, all in stock today.",
  }),
  "indian-clothing-wheeling-il": cityPage({
    city: "Wheeling",
    drive: "a 15-minute drive",
    blurb: "From Wheeling, take Milwaukee Avenue straight down to our Schaumburg boutique — about 15 minutes to the suburbs' fullest Indian ethnic wear collection. Kurtis, sarees, lehengas and menswear, plus same-day DoorDash delivery back to Wheeling.",
  }),
  "indian-clothing-long-grove-il": cityPage({
    city: "Long Grove",
    drive: "a 15-minute drive",
    blurb: "Long Grove shoppers are about 15 minutes from authentic Indian ethnic wear at our Schaumburg boutique — wedding-guest outfits, festive sarees and everyday kurtis to try on in person, with same-day DoorDash delivery when you need it fast.",
  }),
  "indian-clothing-wisconsin": cityPage({
    city: "Wisconsin",
    drive: "an easy drive",
    shipOnly: true,
    blurb: "Wisconsin shoppers — from Milwaukee to Madison to Kenosha — order from our Chicago-area boutique and get authentic Indian ethnic wear shipped to your door in 2–5 days. Southern Wisconsin? We're an easy drive down I-94 if you'd rather browse in person.",
  }),
  "indian-clothing-indiana": cityPage({
    city: "Indiana",
    drive: "an easy drive",
    shipOnly: true,
    blurb: "Indiana shoppers — from Northwest Indiana to Indianapolis — get kurtis, sarees, lehengas and wedding wear shipped from our Chicago-area boutique in 2–5 days. Not sure about sizing? WhatsApp us and we'll help before you order.",
  }),
  "indian-clothing-bloomingdale-il": cityPage({
    city: "Bloomingdale",
    drive: "a 10-minute drive",
    blurb: "Bloomingdale shoppers are barely 10 minutes from our Schaumburg boutique — kurtis, sarees, lehengas and wedding outfits to try on in person, with same-day DoorDash delivery when the occasion sneaks up on you.",
  }),
  "indian-clothing-streamwood-il": cityPage({
    city: "Streamwood",
    drive: "a 10-minute drive",
    blurb: "From Streamwood, hop down Schaumburg Road and you're with us in about 10 minutes. Everyday kurtis to bridal lehengas, menswear and kids' wear — the closest full Indian boutique to Streamwood.",
  }),
  "indian-clothing-hanover-park-il": cityPage({
    city: "Hanover Park",
    drive: "a 10-minute drive",
    blurb: "Hanover Park neighbors — Indian ethnic wear is about 10 minutes away at our Schaumburg boutique. Try everything on in person, or get same-day DoorDash delivery right to your door.",
  }),
  "indian-clothing-bartlett-il": cityPage({
    city: "Bartlett",
    drive: "a 15-minute drive",
    blurb: "From Bartlett, take Lake Street or Route 20 and you're at our Schaumburg boutique in about 15 minutes — a full range of kurtis, sarees, lehengas and party wear, all in stock to try today.",
  }),
  "indian-clothing-rolling-meadows-il": cityPage({
    city: "Rolling Meadows",
    drive: "a 15-minute drive",
    blurb: "Rolling Meadows shoppers: skip the Devon Avenue traffic — our Schaumburg boutique is about 15 minutes down Golf Road, with wedding-guest outfits, festive sarees and everyday kurtis to try on in person.",
  }),
  "indian-clothing-mount-prospect-il": cityPage({
    city: "Mount Prospect",
    drive: "a 15-minute drive",
    blurb: "From Mount Prospect, authentic Indian ethnic wear is about 15 minutes away. Browse kurtis, sarees, Anarkalis and lehengas in person — or order on DoorDash and have your outfit the same day.",
  }),
  "indian-clothing-des-plaines-il": cityPage({
    city: "Des Plaines",
    drive: "a 20-minute drive",
    blurb: "Des Plaines shoppers are about 20 minutes from the Northwest suburbs' fullest Indian boutique — kurtis, sarees, lehengas, menswear and kids' wear, with same-day DoorDash delivery back to Des Plaines.",
  }),
  "indian-clothing-glendale-heights-il": cityPage({
    city: "Glendale Heights",
    drive: "a 20-minute drive",
    blurb: "From Glendale Heights, our Schaumburg boutique is an easy 20-minute drive — one of the largest Indian ethnic wear selections in the suburbs, from everyday kurtis to bridal lehengas.",
  }),
  "indian-clothing-carol-stream-il": cityPage({
    city: "Carol Stream",
    drive: "a 20-minute drive",
    blurb: "Carol Stream shoppers: about 20 minutes to racks of kurtis, sarees and lehengas you can actually try on. In a rush? DoorDash same-day delivery covers Carol Stream too.",
  }),
  "indian-clothing-addison-il": cityPage({
    city: "Addison",
    drive: "a 20-minute drive",
    blurb: "From Addison, take Lake Street up to Schaumburg — about 20 minutes to authentic Indian ethnic wear for the whole family, with same-day delivery back to Addison via DoorDash.",
  }),
  "indian-clothing-inverness-il": cityPage({
    city: "Inverness",
    drive: "a 10-minute drive",
    blurb: "Inverness is one of our closest neighbors — about 10 minutes to the boutique on Golf Road. Wedding-guest outfits, festive sarees and everyday kurtis, all in stock to try on today.",
  }),
  "indian-clothing-skokie-il": cityPage({
    city: "Skokie",
    drive: "a 25-minute drive",
    blurb: "Skokie shoppers — about 25 minutes to our Schaumburg boutique, where you can browse a full Indian ethnic wear collection in person: kurtis, sarees, lehengas, menswear and kids' wear.",
  }),
  "indian-clothing-niles-il": cityPage({
    city: "Niles",
    drive: "a 25-minute drive",
    blurb: "From Niles, authentic Indian ethnic wear is about 25 minutes away in Schaumburg — no need to fight for parking on Devon. Try everything on in person, or order online with fast shipping.",
  }),
  "indian-clothing-park-ridge-il": cityPage({
    city: "Park Ridge",
    drive: "a 25-minute drive",
    blurb: "Park Ridge shoppers are about 25 minutes from the suburbs' favorite Indian boutique. Kurtis, sarees, lehengas and wedding wear — tried on in person or delivered same-day via DoorDash.",
  }),
  "indian-clothing-wheaton-il": cityPage({
    city: "Wheaton",
    drive: "a 30-minute drive",
    blurb: "From Wheaton, our Schaumburg boutique is about 30 minutes up Gary Avenue or I-355 — the closest place to browse a full range of Indian ethnic wear in person, from kurtis to bridal lehengas.",
  }),
  "indian-clothing-aurora-il": cityPage({
    city: "Aurora",
    drive: "a 40-minute drive",
    blurb: "Aurora shoppers make the 40-minute trip to our Schaumburg boutique for the selection — hundreds of kurtis, sarees and lehengas in every size, plus menswear and kids. Prefer home? We ship to Aurora in 2–5 days.",
  }),
};

export const LANDING_PAGES = {
  ...CITY_PAGES,
  "indian-clothing-usa": {
    seoTitle: "Indian Clothing USA — Kurtis, Sarees, Lehengas & Party Wear",
    metaDescription:
      "Shop authentic Indian clothing in the USA: kurtis, sarees, lehengas, party wear & wedding-guest outfits for women, men & kids. In-store near Chicago, shipping nationwide.",
    kicker: "Indian Ethnic Wear · USA",
    h1: "Indian Clothing for Every Celebration, Shipped Across the USA",
    hero: "https://images.unsplash.com/photo-1769063382706-8156b3b33eac?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "Kurti Connection USA brings you a handpicked edit of Indian ethnic wear — from everyday kurtis to show-stopping bridal lehengas. Whether you are dressing for Diwali, a wedding, Navratri or a reception, you will find pieces that feel authentic, modern and beautifully made.",
      "We are a family-run boutique based in Schaumburg, Illinois. Shop with us in person near Chicago, or order online and we will ship your outfit anywhere in the United States.",
    ],
    collection: "women",
    quickLinks: [
      { label: "Kurtis", to: "/shop?collection=kurtis" },
      { label: "Sarees", to: "/shop?collection=sarees" },
      { label: "Lehengas", to: "/shop?collection=lehenga-choli" },
      { label: "Party Wear", to: "/shop?collection=reception-cocktail-party" },
      { label: "Men", to: "/shop?collection=men" },
      { label: "Kids", to: "/shop?collection=kids-girls" },
    ],
    faqs: [
      { q: "Do you ship Indian clothing across the USA?", a: "Yes. We ship nationwide across the United States, with free U.S. shipping on orders over $75." },
      { q: "Can I shop your Indian clothing in person?", a: "Absolutely — visit our boutique at 183 West Golf Rd, Schaumburg, IL, near Chicago. You can also message us on WhatsApp for availability." },
      { q: "What occasions do you cover?", a: "Weddings, receptions, Diwali, Navratri, Eid, Garba, Sangeet, Haldi and everyday festive wear for women, men and kids." },
    ],
    local: true,
  },

  "indian-wedding-guest-dresses": {
    seoTitle: "Indian Wedding Guest Dresses & Outfits (USA)",
    metaDescription:
      "Find the perfect Indian wedding-guest outfit — lehengas, sarees, Anarkalis and gowns for receptions, sangeet and cocktail parties. Shop online in the USA or in-store near Chicago.",
    kicker: "Wedding Guest · Reception · Sangeet",
    h1: "Indian Wedding Guest Dresses You'll Love Being Seen In",
    hero: "https://images.unsplash.com/photo-1756483509254-3cc48a5a15b2?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "Invited to an Indian wedding and not sure what to wear? Our wedding-guest edit makes it easy — elegant lehengas, flowing Anarkalis, statement sarees and cocktail-ready gowns that photograph beautifully and feel comfortable all night.",
      "Not sure about sizing or colour? Message us on WhatsApp and we'll help you pick the right look. " + STORE_LINE,
    ],
    collection: "reception-cocktail-party",
    quickLinks: [
      { label: "Reception & Cocktail", to: "/shop?collection=reception-cocktail-party" },
      { label: "Sangeet", to: "/shop?collection=sangeet-ceremony" },
      { label: "Lehengas", to: "/shop?collection=lehenga-choli" },
      { label: "Sarees", to: "/shop?collection=sarees" },
      { label: "Under $99", to: "/shop?collection=under-99" },
    ],
    faqs: [
      { q: "What should I wear as a guest to an Indian wedding?", a: "Lehengas, Anarkali suits, sarees or Indo-western gowns are all great choices. For daytime events go lighter; for receptions and sangeet, opt for richer colours and embellishment." },
      { q: "Can you help me choose an outfit?", a: "Yes — tap the WhatsApp button and tell us the event, your size and budget. We'll send options and check availability." },
      { q: "Do you have wedding-guest outfits under $99?", a: "Yes, we carry a curated party-wear range under $99, plus premium options for receptions and sangeet nights." },
    ],
    local: true,
  },

  "indian-party-wear": {
    seoTitle: "Indian Party Wear for Women (USA) — Gowns, Suits & Dresses",
    metaDescription:
      "Shop Indian party wear in the USA: festive gowns, Indo-western dresses, Anarkali suits and cocktail outfits for Diwali, receptions and celebrations. In-store near Chicago.",
    kicker: "Party Wear · Festive · Indo-Western",
    h1: "Indian Party Wear That Turns Heads",
    hero: "https://images.unsplash.com/photo-1610048616025-11a3dcc9fd0b?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "From Diwali dinners to birthday celebrations and cocktail evenings, our party-wear collection blends Indian craftsmanship with contemporary silhouettes — think embellished gowns, Indo-western sets and festive Anarkalis.",
      STORE_LINE,
    ],
    collection: "reception-cocktail-party",
    quickLinks: [
      { label: "Party Wear Under $99", to: "/shop?collection=party-wear-under-100" },
      { label: "Reception & Cocktail", to: "/shop?collection=reception-cocktail-party" },
      { label: "Indo-Western", to: "/shop?collection=kaftan" },
      { label: "Sarees", to: "/shop?collection=sarees" },
    ],
    faqs: [
      { q: "What is Indian party wear?", a: "Party wear covers festive and evening outfits — embellished gowns, Indo-western dresses, Anarkali suits and dressy sarees suited to celebrations rather than everyday wear." },
      { q: "Do you carry plus sizes?", a: "Yes, we carry an extended size range including our Plus Size Diva collection. Message us on WhatsApp for fit guidance." },
    ],
    local: true,
  },

  "indian-lehengas": {
    seoTitle: "Indian Lehengas (USA) — Bridal & Party Lehenga Choli",
    metaDescription:
      "Shop Indian lehengas in the USA — bridal lehenga choli, party lehengas and festive sets. Handpicked craftsmanship, in-store near Chicago and shipping nationwide.",
    kicker: "Lehenga Choli · Bridal · Festive",
    h1: "Indian Lehengas — From Everyday Festive to Full Bridal",
    hero: "https://images.unsplash.com/photo-1610047614301-13c63f00c032?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "A great lehenga is unforgettable. Our collection spans lightweight festive sets to intricately embellished bridal lehenga choli — with the fit, flare and finish that make the difference in photos and in person.",
      "Planning a bridal look? Book a visit to our Schaumburg boutique or message us on WhatsApp and we'll guide you through colours, sizing and timelines.",
    ],
    collection: "lehenga-choli",
    quickLinks: [
      { label: "Bridal Collection", to: "/shop?collection=bridal-collection" },
      { label: "Lehenga Choli", to: "/shop?collection=lehenga-choli" },
      { label: "Sangeet", to: "/shop?collection=sangeet-ceremony" },
      { label: "Reception", to: "/shop?collection=reception-cocktail-party" },
    ],
    faqs: [
      { q: "Do you sell bridal lehengas in the USA?", a: "Yes. We stock bridal and party lehenga choli and can help with sizing and coordination. Visit us near Chicago or message us on WhatsApp." },
      { q: "How do I choose the right lehenga size?", a: "Send us your measurements on WhatsApp and we'll recommend the best size and let you know about any fit options." },
    ],
    local: true,
  },

  "indian-sarees": {
    seoTitle: "Indian Sarees (USA) — Party, Festive & Readymade Sarees",
    metaDescription:
      "Shop Indian sarees in the USA — party sarees, festive drapes and easy readymade sarees. Beautiful fabrics and colours, in-store near Chicago and shipping nationwide.",
    kicker: "Sarees · Readymade · Festive",
    h1: "Indian Sarees for Every Occasion",
    hero: "https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "From classic drapes to easy pre-stitched readymade sarees, our saree edit makes it simple to look elegant for weddings, receptions and festive occasions — with a matching blouse to complete the look.",
      STORE_LINE,
    ],
    collection: "sarees",
    quickLinks: [
      { label: "All Sarees", to: "/shop?collection=sarees" },
      { label: "Readymade Sarees", to: "/shop?collection=readymade-saree" },
      { label: "Readymade Blouses", to: "/shop?collection=readymade-blouse" },
      { label: "Reception", to: "/shop?collection=reception-cocktail-party" },
    ],
    faqs: [
      { q: "Do you offer readymade (pre-stitched) sarees?", a: "Yes — readymade sarees are easy to wear and travel-friendly. We also carry matching readymade blouses." },
      { q: "Can I get a matching blouse?", a: "Many sarees pair with our readymade blouse range. Ask us on WhatsApp and we'll help you coordinate." },
    ],
    local: true,
  },

  "indian-kurtis": {
    seoTitle: "Indian Kurtis (USA) — Anarkali, Long & Short Kurtis",
    metaDescription:
      "Shop Indian kurtis in the USA — Anarkali, long kurtis, short kurtis and tunics for everyday and festive wear. In-store near Chicago and shipping nationwide.",
    kicker: "Kurtis · Anarkali · Tunics",
    h1: "Indian Kurtis — Everyday Comfort, Festive Style",
    hero: "https://images.unsplash.com/photo-1759840278381-bf7d5e332050?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "Our kurti collection covers it all — breezy everyday kurtis, elegant long Anarkalis and easy short kurtis and tunics you can dress up or down. Comfortable fabrics, flattering fits and colours you'll reach for again and again.",
      STORE_LINE,
    ],
    collection: "kurtis",
    quickLinks: [
      { label: "Kurtis", to: "/shop?collection=kurtis" },
      { label: "Anarkali / Long Kurtis", to: "/shop?collection=kurti" },
      { label: "Short Kurtis & Tunics", to: "/shop?collection=short-kurtis-tunics" },
      { label: "Under $99", to: "/shop?collection=under-99" },
    ],
    faqs: [
      { q: "What kurti styles do you carry?", a: "Anarkali and long kurtis, short kurtis and tunics — in everyday and festive fabrics, plus an extended size range." },
      { q: "Do you have plus-size kurtis?", a: "Yes, browse our Plus Size Diva range or message us on WhatsApp for recommendations." },
    ],
    local: true,
  },

  "indian-clothing-chicago": {
    seoTitle: "Indian Clothing Store in Chicago (Schaumburg, IL)",
    metaDescription:
      "Looking for Indian clothing in Chicago? Kurti Connection in Schaumburg, IL stocks kurtis, sarees, lehengas & wedding wear. Visit our boutique or shop online.",
    kicker: "Chicago · Schaumburg, IL",
    h1: "Your Indian Clothing Store Near Chicago",
    hero: "https://images.unsplash.com/photo-1654764746225-e63f5e90facd?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "If you're searching for Indian clothing in Chicago, skip the drive downtown. Kurti Connection is right in Schaumburg — an easy stop for shoppers across the northwest suburbs — with a full range of kurtis, sarees, lehengas, party wear and wedding outfits you can see and try in person.",
      "We proudly serve Schaumburg, Hoffman Estates, Arlington Heights, Bloomingdale, Streamwood, Elgin, Naperville and the wider Chicago area.",
    ],
    collection: "new-arrivals",
    quickLinks: [
      { label: "New Arrivals", to: "/shop?collection=new-arrivals" },
      { label: "Sarees", to: "/shop?collection=sarees" },
      { label: "Lehengas", to: "/shop?collection=lehenga-choli" },
      { label: "Party Wear", to: "/shop?collection=reception-cocktail-party" },
    ],
    faqs: [
      { q: "Where is your store near Chicago?", a: "We're at 183 West Golf Rd, Schaumburg, IL 60195 — about 30 miles northwest of downtown Chicago and easy to reach from the suburbs." },
      { q: "What are your store hours?", a: "Monday 2:00–7:00 PM, Tuesday–Friday 11:30 AM–7:00 PM, and Saturday & Sunday 11:30 AM–6:00 PM." },
      { q: "Can I call or WhatsApp before visiting?", a: "Yes — call or WhatsApp us at +1 (224) 246-1461 to check availability before you come in." },
    ],
    local: true,
  },

  "indian-boutique-schaumburg": {
    seoTitle: "Indian Boutique in Schaumburg, IL — Kurti Connection",
    metaDescription:
      "Kurti Connection is an Indian fashion boutique in Schaumburg, IL. Shop kurtis, sarees, lehengas, jewelry & wedding wear in-store, or order online across the USA.",
    kicker: "Schaumburg, IL · Since 2016",
    h1: "An Indian Fashion Boutique in the Heart of Schaumburg",
    hero: "https://images.unsplash.com/photo-1759840278478-826c0d0f110e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "Kurti Connection is a much-loved Indian fashion boutique in Schaumburg, Illinois. Walk in to browse kurtis, sarees, lehengas, party wear, dupattas, blouses and jewelry — with friendly, personal service and pieces for the whole family.",
      "With a decade of dressing the local community for weddings and festivals, we know how to help you find the right outfit for your occasion.",
    ],
    collection: "new-arrivals",
    quickLinks: [
      { label: "New Arrivals", to: "/shop?collection=new-arrivals" },
      { label: "Jewelry & Accessories", to: "/shop?collection=jewelry-and-accessories" },
      { label: "Necklace Sets", to: "/shop?collection=necklace-sets" },
      { label: "Kurtis", to: "/shop?collection=kurtis" },
    ],
    faqs: [
      { q: "What does your Schaumburg boutique sell?", a: "Indian ethnic wear for women, men and kids — kurtis, sarees, lehengas, party wear, dupattas, readymade blouses and jewelry." },
      { q: "Do you offer in-store shopping?", a: "Yes, come visit us at 183 West Golf Rd, Schaumburg, IL. You can also shop everything online." },
    ],
    local: true,
  },

  "indian-dresses-near-me": {
    seoTitle: "Indian Dresses Near Me — Boutique in Schaumburg, IL",
    metaDescription:
      "Searching 'Indian dresses near me'? Kurti Connection in Schaumburg, IL has kurtis, lehengas, sarees & party wear to try in-store, plus online shipping across the USA.",
    kicker: "Near You · Chicago Suburbs",
    h1: "Indian Dresses Near You in the Chicago Suburbs",
    hero: "https://images.unsplash.com/photo-1759840278478-826c0d0f110e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "Want to see and try Indian dresses in person before you buy? Visit Kurti Connection in Schaumburg — convenient for shoppers across the northwest Chicago suburbs — and browse kurtis, lehengas, sarees, gowns and party wear in one place.",
      "Prefer to shop from home? Order online and we'll ship anywhere in the USA, or message us on WhatsApp for a quick, personal recommendation.",
    ],
    collection: "party-wear-under-100",
    quickLinks: [
      { label: "Party Wear", to: "/shop?collection=reception-cocktail-party" },
      { label: "Lehengas", to: "/shop?collection=lehenga-choli" },
      { label: "Sarees", to: "/shop?collection=sarees" },
      { label: "Kurtis", to: "/shop?collection=kurtis" },
    ],
    faqs: [
      { q: "Is there an Indian dress store near me?", a: "If you're in the Chicago area, Kurti Connection in Schaumburg, IL is easy to reach from Hoffman Estates, Arlington Heights, Bloomingdale, Streamwood, Elgin and Naperville." },
      { q: "Can I try dresses on in-store?", a: "Yes — visit our Schaumburg boutique to try outfits in person, or ask us for size guidance on WhatsApp." },
    ],
    local: true,
  },

  "indian-wedding-guest-clothing": {
    seoTitle: "Indian Wedding Guest Clothing for Women & Men (USA)",
    metaDescription:
      "Shop Indian wedding guest clothing in the USA — lehengas, sarees, gowns & kurta sets for women and men. In-store near Chicago, shipping nationwide.",
    kicker: "Wedding Guest · Women · Men",
    h1: "Indian Wedding Guest Clothing for Women & Men",
    hero: "https://images.unsplash.com/photo-1756483509254-3cc48a5a15b2?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "One invitation, two wardrobes to plan. Our wedding-guest edit covers both of you — lehengas, Anarkalis, sarees and gowns for her, kurta sets and kurta-with-jacket looks for him — for the ceremony, sangeet and reception.",
      "Shopping together? Visit the boutique and try everything side by side, or message us on WhatsApp with your event date and we'll coordinate both outfits. " + STORE_LINE,
    ],
    collection: "reception-cocktail-party",
    quickLinks: [
      { label: "Women's Wedding Guest", to: "/indian-wedding-guest-dresses" },
      { label: "Men's Outfits", to: "/mens-wedding-guest-outfits" },
      { label: "Lehengas", to: "/shop?collection=lehenga-choli" },
      { label: "Men's Kurta + Jacket", to: "/shop?collection=mens-kurta-with-jacket" },
      { label: "Guide: Winter Weddings", to: "/guide/what-to-wear-indian-wedding-winter-chicago" },
      { label: "Guide: Lehenga vs Saree", to: "/guide/lehenga-vs-saree-vs-anarkali" },
    ],
    faqs: [
      { q: "What should women wear to an Indian wedding as a guest?", a: "Lehengas, Anarkali suits, sarees or Indo-western gowns are all perfect. Daytime ceremonies suit lighter fabrics; receptions and sangeet call for richer colours and embellishment." },
      { q: "What should men wear to an Indian wedding as a guest?", a: "A kurta set is the classic choice — dress it up with a Nehru jacket for the ceremony or sangeet. Avoid red and white-heavy outfits, which are traditionally the couple's colours." },
      { q: "Can you outfit both of us for the same wedding?", a: "Yes — couples shop together in-store all the time. We can coordinate colours across women's and men's outfits, and handle any alterations in-house." },
    ],
    local: true,
  },

  "mens-wedding-guest-outfits": {
    seoTitle: "Men's Indian Wedding Guest Outfits (USA) — Kurta Sets",
    metaDescription:
      "Men's Indian wedding guest outfits in the USA: kurta sets, kurta-with-jacket looks & festive menswear for sangeet, ceremony and reception. Near Chicago, shipping nationwide.",
    kicker: "Men · Kurta Sets · Nehru Jackets",
    h1: "Men's Indian Wedding Guest Outfits",
    hero: "https://images.unsplash.com/photo-1617137968427-85924c800a22?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "Getting the men's side of the invitation right is easy here — well-cut kurta sets, kurta-with-jacket combinations and festive menswear that look sharp in the ceremony photos and stay comfortable through the reception.",
      "Unsure about sizing or what suits the event? Message us on WhatsApp with the dress code and your measurements. " + STORE_LINE,
    ],
    collection: "men",
    quickLinks: [
      { label: "All Men's", to: "/shop?collection=men" },
      { label: "Kurta + Jacket", to: "/shop?collection=mens-kurta-with-jacket" },
      { label: "Women's Guest Edit", to: "/indian-wedding-guest-dresses" },
    ],
    faqs: [
      { q: "What do men wear to an Indian wedding?", a: "A kurta pajama set works for most events; add a Nehru jacket for the ceremony or sangeet to dress it up. Steer clear of red and heavy white — those belong to the groom and bride." },
      { q: "Do you carry men's Indian clothing in-store?", a: "Yes — our Schaumburg boutique stocks men's kurta sets and kurta-with-jacket outfits you can try on in person, with staff to help with fit." },
    ],
    local: true,
  },

  "saree-blouses": {
    seoTitle: "Saree Blouses (USA) — Readymade Sari Blouses That Fit",
    metaDescription:
      "Shop readymade saree blouses in the USA — sari blouses in every size, with in-house alterations for a perfect fit. In-store near Chicago, shipping nationwide.",
    kicker: "Readymade Blouses · Perfect Fit",
    h1: "Saree Blouses That Actually Fit",
    hero: "https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    intro: [
      "The blouse makes the saree. Our readymade sari blouses come in a full size range with necklines and sleeve options for every drape — and if the fit is nearly-right, our in-house alteration service finishes it to your measurements.",
      "Pairing a blouse with a saree you already own? Bring it to the boutique or send us a photo on WhatsApp and we'll help you match colour and fabric. " + STORE_LINE,
    ],
    collection: "readymade-blouse",
    quickLinks: [
      { label: "Readymade Blouses", to: "/shop?collection=readymade-blouse" },
      { label: "Sarees", to: "/shop?collection=sarees" },
      { label: "Readymade Sarees", to: "/shop?collection=readymade-saree" },
      { label: "Guide: Blouse Measuring", to: "/guide/saree-blouse-measurement-guide" },
    ],
    faqs: [
      { q: "Do you alter saree blouses?", a: "Yes — we offer in-house alterations on blouses and outfits purchased from us, so a readymade blouse can be finished to your measurements." },
      { q: "How do I pick a readymade blouse size?", a: "Measure your bust and match it to the size chart, or message us on WhatsApp with your measurements and we'll recommend the size and any alteration needed." },
      { q: "Can you match a blouse to my saree?", a: "Send us a photo of your saree on WhatsApp or bring it to the boutique — we'll help you find the closest colour and fabric match." },
    ],
    local: true,
  },
};

export const getLanding = (slug) => LANDING_PAGES[slug] || null;
